import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { DateRange } from 'react-day-picker';
import { useForm, useWatch } from 'react-hook-form';
import { useCustomToast } from '@/hooks/use-custom-toast';
import { useSimpleModal } from '@/features/_shared/hooks/use-handle-local-modal';
import { axiosExtractErrorMessage, AxiosErrorCustom } from '@/lib/axios';
import { queryClient } from '@/lib/react-query';
import { useDebounce } from '@/hooks/use-debounce';
import { useListTicketMemberQuery } from '../../../api/get-ticket-member.api';
import { useListLabelTicketQuery } from '@/features/ticketings/label-ticketing/api/get-list-label-ticket.api';
import { useListCaseQuery } from '@/features/cases/case/api/get-list-case.api';
import { useListSpacesQuery } from '@/features/ticketings/spaces/space/api/get-list-space.api';
import { useGetListColumnQuery } from '@/features/ticketings/columns/column/api/get-list-column.api';
import { useSingleTicketQuery, GET_SINGLE_TICKET_QUERY_KEY } from '../../../api/get-single-ticket.api';
import { useListTicketBySpaceQuery } from '../../../api/get-ticket-by-space.api';
import {
  TUpdateTicketValidation,
  UpdateTicketValidationSchema,
  useUpdateTicketMutation,
} from '../../../api/update-ticket.api';
import { IUpdateTicketModalProps } from './update-ticket-modal';
import { useTicketingModalRootActions } from '@/features/ticketings/_shared/components/ticketing-modal-root/ticketing-modal-root.store';
import { useUpdateTicketAttachments } from './use-update-ticket-attachments';
import { areArraysEqual, deepEqual } from '@/utils/array-utils';
import { handleDomBubbleEvent } from '@/utils/dom';
import { getPositionAtTop } from '@/features/ticketing/tickets/utils/ordering-ticket.utils';
import { MessageSquareText } from 'lucide-react';
import { ClockFadingIcon } from '@/components/ui/_refactor/icons';
import useSelectedTicketQS from '../../../hooks/use-selected-ticket-qs';
import useShareTicket from '../../../hooks/use-share-ticket';

export function useUpdateTicketModal({ spaceId, spaceColumnId, ticketId, isMarkAsCompleted }: IUpdateTicketModalProps) {
  // ===== Hooks =====
  const { handleSelectedTicket } = useSelectedTicketQS();
  const showToast = useCustomToast();
  const modalActions = useTicketingModalRootActions();

  // ===== Tabs =====
  const tabs = useMemo(() => {
    return [
      {
        label: 'Comments',
        value: 'comments',
        icon: MessageSquareText,
        count: 0,
        isLoading: false,
      },
      {
        label: 'Activity',
        value: 'activity',
        icon: ClockFadingIcon,
        count: 0,
        isLoading: false,
      },
    ];
  }, []);

  // ===== State =====
  const [searchCase, setSearchCase] = useState('');
  const debouncedSearchCase = useDebounce(searchCase, 300);

  // ===== Form =====
  const form = useForm<TUpdateTicketValidation>({
    resolver: zodResolver(UpdateTicketValidationSchema),
    mode: 'onChange',
    defaultValues: {
      spaceId: '',
      ticketId: '',
      spaceColumnId: '',
      position: '',
      title: '',
      startDate: undefined,
      endDate: undefined,
      content: undefined,
      labelTicketIds: [],
      userIds: [],
      caseIds: [],
      attachments: [],
      isCompleted: false,
      note: undefined,
    },
  });

  // Watch form values to avoid multiple form.watch() calls
  const watchedFormValues = useWatch({ control: form.control });
  const watchedSpaceId = useWatch({ control: form.control, name: 'spaceId' });
  const watchedSpaceColumnId = useWatch({ control: form.control, name: 'spaceColumnId' });
  const watchedTitle = useWatch({ control: form.control, name: 'title' });
  const watchedIsCompleted = useWatch({ control: form.control, name: 'isCompleted' });

  // ===== Init Guard =====
  const isFormInitialized = useRef(false);
  const previousSpaceColumnId = useRef<string | undefined>(spaceColumnId);
  const originalFormValuesRef = useRef<TUpdateTicketValidation | null>(null);

  // ===== Track Initial isCompleted Value =====
  const initialIsCompletedRef = useRef<boolean | null>(null);
  const previousTicketIdRef = useRef<string | null>(null);

  // ===== Date Modal =====
  const {
    values: dateValues,
    isOpen: isOpenDateModal,
    handleSetValuesModal: handleSetDateValuesModal,
    handleOpenModal: handleOpenDateModal,
    handleCloseModal: handleCloseDateModal,
  } = useSimpleModal<DateRange | null>({
    from: undefined,
    to: undefined,
  });

  // ===== Attachments Hook =====
  const attachments = useUpdateTicketAttachments({ form });

  // ===== Data Queries =====
  // Single Ticket
  const {
    data: singleTicketQuery,
    isLoading: isLoadingSingleTicketQuery,
    refetch: refetchSingleTicket,
    error: singleTicketError,
  } = useSingleTicketQuery({
    params: { ticketId },
    queryConfig: {
      enabled: !!ticketId,
      staleTime: 0,
      gcTime: 0,
    },
  });
  const ticketData = useMemo(() => {
    if (!singleTicketQuery?.payload || isLoadingSingleTicketQuery) return null;
    return singleTicketQuery.payload;
  }, [singleTicketQuery, isLoadingSingleTicketQuery]);

  const createdAt = useMemo(() => {
    if (!singleTicketQuery?.payload?.createdAt) return '';
    // Format the date - you may need to adjust this based on your date formatting utility
    return new Date(singleTicketQuery.payload.createdAt).toLocaleDateString();
  }, [singleTicketQuery?.payload?.createdAt]);

  const isArchived = useMemo(() => {
    if (!singleTicketQuery?.payload?.isArchived) return false;
    return singleTicketQuery.payload.isArchived;
  }, [singleTicketQuery?.payload?.isArchived]);

  // List Tickets By Space (to get tickets in columns for position calculation)
  const { data: listTicketBySpaceQuery } = useListTicketBySpaceQuery({
    params: { spaceId },
    queryConfig: { enabled: !!spaceId },
  });

  // Ticket Members
  const { data: listTicketMemberData, isLoading: isLoadingListTicketMemberData } = useListTicketMemberQuery({
    params: { page: 1, limit: 500, spaceId, search: '' },
    queryConfig: { enabled: !!spaceId },
    select: (data) => data.payload?.rows || [],
  });

  // Cases
  const { data: listCaseData, isLoading: isLoadingListCaseData } = useListCaseQuery({
    params: { page: 1, limit: 500, search: debouncedSearchCase },
    queryConfig: { enabled: !!spaceId },
    select: (data) => data.payload?.rows || [],
  });

  // Label Tickets
  const { data: listLabelTicketData, isLoading: isLoadingListLabelTicketData } = useListLabelTicketQuery({
    params: { page: 1, limit: 500 },
    queryConfig: { enabled: !!spaceId },
    select: (data) => data.payload?.rows || [],
  });

  // Spaces
  const { data: listSpacesData, isLoading: isLoadingListSpacesData } = useListSpacesQuery({
    params: { page: 1, limit: 500 },
    queryConfig: { enabled: true },
    select: (data) => data.payload || [],
  });

  // Space Columns
  const { data: listSpaceColumnsData, isLoading: isLoadingListSpaceColumnsData } = useGetListColumnQuery({
    params: { spaceId: watchedSpaceId || '' },
    queryConfig: { enabled: !!watchedSpaceId },
    select: (data) => data.payload || [],
  });

  // ===== Mutations =====
  // Update Ticket
  const mutationUpdateTicket = useUpdateTicketMutation({
    mutationConfig: {
      onError: (error: AxiosErrorCustom) => {
        const errorMessage = axiosExtractErrorMessage(error) || 'Error update ticketing ticket, please try again';
        showToast({ message: errorMessage, type: 'error' });
      },
      onSuccess: async () => {
        showToast({ message: 'Ticketing ticket updated successfully!', type: 'success' });
        modalActions.closeModal('UpdateTicketModal');
        form.reset();
        attachments.clearFile();

        // Invalidate queries
        if (spaceId && ticketId) {
          await queryClient.invalidateQueries({
            queryKey: [GET_SINGLE_TICKET_QUERY_KEY, { ticketId }],
          });
        }
      },
    },
  });

  // ===== Modal State =====
  const isUpdateTicketOpen = useMemo(() => {
    // Don't show modal if there's a 403 error (prevents flash before redirect)
    if (singleTicketError) {
      const axiosError = singleTicketError as AxiosErrorCustom;
      const status = axiosError?.response?.status;
      if (status === 403) {
        return false;
      }
    }
    return modalActions.checkIsOpenModal('UpdateTicketModal');
  }, [modalActions, singleTicketError]);

  // ===== Computed Values =====
  const initialIsCompleted = initialIsCompletedRef.current;
  const isButtonDisabled = initialIsCompleted === true;

  // ===== Effects =====
  // Handle 403 errors - close modal immediately to prevent flash
  useEffect(() => {
    if (singleTicketError) {
      const axiosError = singleTicketError as AxiosErrorCustom;
      const status = axiosError?.response?.status;

      // Close modal immediately on 403 to prevent modal flash before redirect
      if (status === 403) {
        modalActions.closeModal('UpdateTicketModal');
      }
    }
  }, [singleTicketError, modalActions]);

  // Reset init guard when ticketId changes
  useEffect(() => {
    isFormInitialized.current = false;
    originalFormValuesRef.current = null;
    previousSpaceColumnId.current = spaceColumnId;
  }, [ticketId, spaceColumnId]);

  // Track the initial isCompleted value from backend
  useEffect(() => {
    // Reset when ticketId changes
    if (previousTicketIdRef.current !== ticketId) {
      initialIsCompletedRef.current = null;
      previousTicketIdRef.current = ticketId;
    }

    // Capture the initial value when ticket data is loaded
    // Only capture if ticketId exists and form has been populated with ticket data (check if title exists)
    const hasTicketData = Boolean(watchedTitle); // Title is always set when ticket data loads

    if (
      initialIsCompletedRef.current === null &&
      ticketId &&
      hasTicketData &&
      typeof watchedIsCompleted === 'boolean'
    ) {
      initialIsCompletedRef.current = watchedIsCompleted;
    }
  }, [ticketId, watchedTitle, watchedIsCompleted]);

  // Initialize form with ticket data
  useEffect(() => {
    if (ticketData && !isLoadingSingleTicketQuery && !isFormInitialized.current) {
      const initialFormValues: TUpdateTicketValidation = {
        spaceId,
        ticketId: ticketData?.ticketId || '',
        spaceColumnId: ticketData?.spaceColumn?.spaceColumnId || '',
        position: ticketData?.position || '',
        title: ticketData.title || '',
        startDate: ticketData.startDate || '',
        endDate: ticketData.endDate || '',
        content: ticketData.content || '',
        labelTicketIds: [...(ticketData.ticketLabels?.map((l) => l.labelTicketId) || [])],
        userIds: [...(ticketData.users?.map((u) => u.userId) || [])],
        caseIds: [...(ticketData.cases?.map((c) => c.caseId) || [])],
        attachments: [...(ticketData.attachments || [])],
        isCompleted: ticketData.isCompleted,
        ...(ticketData?.note && { note: ticketData.note }),
      };

      form.reset(initialFormValues);

      // Store the original values for comparison
      originalFormValuesRef.current = initialFormValues;

      if (ticketData.startDate && ticketData.endDate) {
        handleSetDateValuesModal({
          from: new Date(ticketData.startDate),
          to: new Date(ticketData.endDate),
        });
      }

      // Update the ref to track the initial spaceColumnId
      previousSpaceColumnId.current = spaceColumnId;
      isFormInitialized.current = true;
    }
  }, [ticketData, isLoadingSingleTicketQuery, spaceColumnId, ticketId, handleSetDateValuesModal, form, spaceId]);

  // Sync form's isCompleted when ticketData changes after initialization (e.g., after marking as complete)
  const ticketIsCompleted = useMemo(() => ticketData?.isCompleted, [ticketData?.isCompleted]);
  useEffect(() => {
    if (ticketData && isFormInitialized.current && typeof ticketIsCompleted === 'boolean') {
      const currentFormIsCompleted = form.getValues('isCompleted');
      // Only update if the values differ to avoid unnecessary updates
      if (currentFormIsCompleted !== ticketIsCompleted) {
        form.setValue('isCompleted', ticketIsCompleted);
        // Update the initialIsCompletedRef if it's different
        if (initialIsCompletedRef.current !== ticketIsCompleted) {
          initialIsCompletedRef.current = ticketIsCompleted;
        }
      }
    }
  }, [ticketData, ticketIsCompleted, form]);

  // Handle position update when spaceColumnId changes
  useEffect(() => {
    // Only run if form is initialized and we have ticket data
    if (!isFormInitialized.current || !ticketData) {
      // Update the ref when form is not initialized yet
      if (watchedSpaceColumnId) {
        previousSpaceColumnId.current = watchedSpaceColumnId;
      }
      return;
    }

    const currentSpaceColumnId = watchedSpaceColumnId;
    const prevSpaceColumnId = previousSpaceColumnId.current;

    // If spaceColumnId changed to a different column, set position to top of the new column
    if (currentSpaceColumnId && currentSpaceColumnId !== prevSpaceColumnId) {
      // Get tickets for the target column from the list ticket by space query
      const targetColumn = listTicketBySpaceQuery?.payload?.find((col) => col.spaceColumnId === currentSpaceColumnId);

      // Get tickets for the target column, excluding the current ticket being updated
      const targetColumnTickets = targetColumn?.tickets?.filter((ticket) => ticket.ticketId !== ticketId) || [];

      // Calculate position at top using fractional-indexing ordering utils
      const topPosition = getPositionAtTop(
        targetColumnTickets.map((t) => ({
          ticketId: t.ticketId,
          position: (t as { position?: string | null }).position ?? null,
        }))
      );
      form.setValue('position', topPosition);
      previousSpaceColumnId.current = currentSpaceColumnId;

      // Update the originalFormValuesRef to reflect the auto-calculated position
      // This prevents position from being detected as a change when it's auto-updated
      if (originalFormValuesRef.current) {
        originalFormValuesRef.current = {
          ...originalFormValuesRef.current,
          position: topPosition,
        };
      }
    }
  }, [watchedSpaceColumnId, ticketData, form, listTicketBySpaceQuery, ticketId]);

  // Check if form has changes compared to original data
  const hasFormChanges = useMemo(() => {
    // Don't check for changes if form hasn't been initialized yet
    if (!ticketData || !isFormInitialized.current || !originalFormValuesRef.current) return false;

    const currentValues = watchedFormValues as TUpdateTicketValidation;
    if (!currentValues) return false;

    // Use the stored original values from when form was initialized
    const originalValues = originalFormValuesRef.current;

    // Check scalar fields
    // Note: 'position' is excluded because it's automatically calculated when spaceColumnId changes
    // We only need to check spaceColumnId - if it changes, position will be updated accordingly
    const scalarFields: Array<keyof TUpdateTicketValidation> = [
      'title',
      'startDate',
      'endDate',
      'content',
      'spaceColumnId',
      'isCompleted',
      'note',
    ];
    const hasScalarChanges = scalarFields.some((key) => {
      return !deepEqual(currentValues[key], originalValues[key]);
    });

    // Check array fields
    const hasLabelChanges = !areArraysEqual(currentValues.labelTicketIds || [], originalValues.labelTicketIds || [], {
      ignoreOrder: true,
    });

    const hasUserChanges = !areArraysEqual(currentValues.userIds || [], originalValues.userIds || [], {
      ignoreOrder: true,
    });

    const hasCaseChanges = !areArraysEqual(currentValues.caseIds || [], originalValues.caseIds || [], {
      ignoreOrder: true,
    });

    // Check attachments
    const hasAttachmentChanges = !areArraysEqual(currentValues.attachments || [], originalValues.attachments || [], {
      comparator: (a, b) => a.uri === b.uri,
      ignoreOrder: true,
    });

    return hasScalarChanges || hasLabelChanges || hasUserChanges || hasCaseChanges || hasAttachmentChanges;
  }, [watchedFormValues, ticketData]);

  // ===== Handlers =====
  // Share Ticket
  const { handleShareTicket } = useShareTicket({
    spaceId,
    ticketId,
    isArchived,
  });

  // Close Modal
  const handleCloseModal = useCallback(() => {
    modalActions.closeModal('UpdateTicketModal');
    handleSelectedTicket('');
    form.reset();
    attachments.clearFile();
  }, [modalActions, form, attachments]);

  // Confirmation modal state
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

  const handleOpenConfirmModal = useCallback(() => {
    setIsConfirmModalOpen(true);
  }, []);

  const handleCloseConfirmModal = useCallback(() => {
    setIsConfirmModalOpen(false);
  }, []);

  const handleConfirmDiscard = useCallback(() => {
    setIsConfirmModalOpen(false);
    handleCloseModal();
  }, [handleCloseModal]);

  const HandleCloseModalWithConfirmation = useCallback(() => {
    // Check if there are unsaved changes
    if (hasFormChanges) {
      // Open confirmation modal
      handleOpenConfirmModal();
    } else {
      handleCloseModal();
    }
  }, [handleCloseModal, hasFormChanges, handleOpenConfirmModal]);

  // Handle Mark as Complete Ticket
  const handleMarkAsCompleteTicket = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      if (isButtonDisabled) return;
      handleDomBubbleEvent(e);
      const currentIsCompleted = form.watch('isCompleted');

      // If uncompleting (from true to false), just toggle directly
      if (currentIsCompleted) {
        form.setValue('isCompleted', false);
        return;
      }

      // If marking as complete (from false to true), show confirmation first
      if (!currentIsCompleted) {
        modalActions.openModal('CompleteTicketModal', {
          spaceId,
          ticketId,
          onSuccess: async () => {
            // Refetch the ticket data first
            const { data: refetchedData } = await refetchSingleTicket();

            // Update the form to reflect that the ticket is now completed
            if (refetchedData?.payload) {
              // Update isCompleted in the form
              form.setValue('isCompleted', true);

              // Update the initialIsCompletedRef to reflect the new state
              initialIsCompletedRef.current = true;

              // Optionally update other form fields if needed to keep everything in sync
              // The form will be fully refreshed when the effect runs with new ticketData
            } else {
              // Fallback: just update the form value if refetch fails
              form.setValue('isCompleted', true);
              initialIsCompletedRef.current = true;
            }
          },
        });
      }
    },
    [isButtonDisabled, form, modalActions, spaceId, ticketId, refetchSingleTicket]
  );

  // Handle Archive Ticket
  const handleArchiveTicket = useCallback(() => {
    modalActions.openModal('ArchiveTicketModal', {
      spaceId,
      ticketId,
      onStart: async () => {
        modalActions.closeModal('UpdateTicketModal');
      },
      onSuccess: async () => {
        form.reset();
        attachments.clearFile();

        // Invalidate queries
        if (spaceId && ticketId) {
          await queryClient.invalidateQueries({
            queryKey: [GET_SINGLE_TICKET_QUERY_KEY, { ticketId }],
          });
        }
      },
    });
  }, [modalActions, spaceId, ticketId, form, attachments, queryClient]);

  // Handle Restore Ticket
  const handleRestoreTicket = useCallback(() => {
    modalActions.openModal('RestoreTicketModal', {
      spaceId,
      ticketId,
      onStart: async () => {
        modalActions.closeModal('UpdateTicketModal');
      },
      onSuccess: async () => {
        form.reset();
        attachments.clearFile();
      },
    });
  }, [modalActions, spaceId, ticketId, form, attachments, queryClient]);

  // Submit Form
  const handleSubmitForm = useCallback(
    (data: TUpdateTicketValidation) => {
      if (!ticketData) return;

      // Original values (from DB)
      const originalValues = {
        spaceColumnId: ticketData?.spaceColumn?.spaceColumnId || '',
        position: ticketData?.position || '',
        title: ticketData.title || '',
        startDate: ticketData.startDate || '',
        endDate: ticketData.endDate || '',
        content: ticketData.content || '',
        labelTicketIds: ticketData.ticketLabels?.map((l) => l.labelTicketId) || [],
        userIds: ticketData.users?.map((u) => u.userId) || [],
        caseIds: ticketData.cases?.map((c) => c.caseId) || [],
        attachments: ticketData.attachments || [],
        isCompleted: ticketData.isCompleted,
        note: ticketData.note || '',
      };

      // Initialize payload with required IDs
      const dataToUpdate: {
        ticketId: string;
        spaceId: string;
      } & Partial<TUpdateTicketValidation> = {
        ticketId,
        spaceId,
      };

      // Compare and add only changed scalar fields
      (['title', 'startDate', 'endDate', 'content', 'isCompleted', 'note'] as const).forEach((key) => {
        if (!deepEqual(data[key], originalValues[key])) {
          (dataToUpdate as Record<string, unknown>)[key] = data[key];
        }
      });

      // Compare space column id
      const originalSpaceColumnId = originalValues.spaceColumnId || '';
      const dataSpaceColumnId = data.spaceColumnId || '';

      if (originalSpaceColumnId !== dataSpaceColumnId) {
        dataToUpdate.spaceColumnId = data.spaceColumnId;
        dataToUpdate.position = data.position;
      }

      // Compare and add only changed array fields
      if (!areArraysEqual(data.labelTicketIds || [], originalValues.labelTicketIds, { ignoreOrder: true })) {
        dataToUpdate.labelTicketIds = data.labelTicketIds;
      }

      if (!areArraysEqual(data.userIds || [], originalValues.userIds, { ignoreOrder: true })) {
        dataToUpdate.userIds = data.userIds;
      }

      if (!areArraysEqual(data.caseIds || [], originalValues.caseIds, { ignoreOrder: true })) {
        dataToUpdate.caseIds = data.caseIds;
      }

      // Compare attachments (using URI or URL)
      if (
        !areArraysEqual(data.attachments || [], originalValues.attachments, {
          comparator: (a, b) => a.uri === b.uri,
          ignoreOrder: true,
        })
      ) {
        dataToUpdate.attachments = data.attachments;
      }

      // Mutate only changed fields
      mutationUpdateTicket.mutate(dataToUpdate);
    },
    [mutationUpdateTicket, ticketId, spaceColumnId, ticketData]
  );

  // console.log(form.watch())
  // console.log(form.formState.errors);
  // console.log(form.formState.isValid);
  // console.log('hasFormChanges', hasFormChanges);

  // ===== Return =====
  return {
    // Tabs
    tabs,

    // Modal state
    isMarkAsCompleted,
    isUpdateTicketOpen,
    handleCloseModal,
    HandleCloseModalWithConfirmation,

    // Form
    createdAt,
    form,
    handleSubmitForm,
    isLoadingUpdateTicket: Boolean(mutationUpdateTicket?.isPending),
    hasFormChanges,
    initialIsCompleted,
    isButtonDisabled,

    // Date modal
    dateValues,
    isOpenDateModal,
    handleOpenDateModal,
    handleCloseDateModal,
    handleSetDateValuesModal,

    // Ticket members
    listTicketMemberOptions: listTicketMemberData || [],
    isLoadingListTicketMemberData,

    // Cases
    listCaseOptions: listCaseData || [],
    isLoadingListCaseData,
    handleSearchCase: setSearchCase,

    // Label tickets
    listLabelTicketOptions: listLabelTicketData || [],
    isLoadingListLabelTicketData,

    // Spaces
    listSpacesOptions: listSpacesData || [],
    isLoadingListSpacesData,

    // Space columns
    listSpaceColumnsOptions: listSpaceColumnsData || [],
    isLoadingListSpaceColumnsData,

    // Files
    extractedAttachments: attachments.extractedAttachments,
    uploadingFileIds: attachments.uploadingFileIds,
    isUploadingFiles: attachments.isUploadingFiles,
    handleAddFiles: attachments.handleAddFiles,
    handleRemoveFile: attachments.handleRemoveFile,

    // Share
    handleShareTicket,

    // Loading
    isLoadingSingleTicketQuery,

    // Confirmation Modal
    isConfirmModalOpen,
    handleCloseConfirmModal,
    handleConfirmDiscard,

    // IDs
    spaceId,
    spaceColumnId,
    ticketId,

    // Handlers
    handleMarkAsCompleteTicket,
    isArchived,
    handleArchiveTicket,
    handleRestoreTicket,
  };
}

export type UseUpdateTicketModalReturn = ReturnType<typeof useUpdateTicketModal>;
